/* eslint-disable complexity */
/*eslint max-lines-per-function: ["error", 200]*/
import React from 'react';
import { Badge, Button, Tooltip } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import * as Icon from 'react-bootstrap-icons';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import momencik from '../../../common/momencik';
import { GetExamDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import { ExaminedFromExamPreviewLink } from '../Examined/ExaminedFromExamPreviewLink';
import { VisitRegistrationLink } from '../../containers/RegistrationsView/VisitRegistrationLink';

import { ExamPreviewLink } from './ExamPreviewLink';
import { ExamEditLink } from './ExamEditLink';
import { ExamChatLink } from './ExamChatLink';
import { ExamRemoveFromListLink } from './ExamRemoveFromListLink';

interface ExamSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
  initialOrderColumn?: string;
  initialOrderDirection?: 'DESC' | 'ASC';
  withFilterInColumns?: boolean;
  withoutButtons?: boolean;
  enableMultiSelect?: boolean;
  selectedKeys?: string[];
  onRemove?: (key: string) => void;
}

export const examForGrid = (
  exams: GetExamDto,
  onRemove?: (key: string) => void,
) => {
  const token = localStorage.getItem('mzt-token') || '';
  return {
    key: exams.id,
    values: {
      'exams.online': (
        <>
          {exams?.online && (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Badanie online!</Tooltip>}
            >
              <Button variant="link">
                <Icon.Globe2 />
              </Button>
            </OverlayTrigger>
          )}
          {exams?.external && !exams?.online && (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Skierowanie zewnętrzne!</Tooltip>}
            >
              <Button variant="link">
                <Icon.ArrowUpRight />
              </Button>
            </OverlayTrigger>
          )}
          {exams?.visitId && (
            <VisitRegistrationLink
              visitId={exams.visitId}
              icon={Icon.Calendar}
              externalBooking={!!exams.visit?.externalBooking}
            />
          )}
        </>
      ),
      'exams.number': (
        <>
          <Badge bg="info" className="me-1">
            {exams.number}
          </Badge>
          {exams?.active && <ExamEditLink examId={exams.id} />}
          {exams?.alarm && !exams.endDate && (
            <>
              {' '}
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Badany prosi o pomoc!</Tooltip>}
              >
                <Badge bg="danger" className="me-1">
                  !
                </Badge>
              </OverlayTrigger>
            </>
          )}
        </>
      ),
      'examined.surname': exams.examined && (
        <>
          <ExaminedFromExamPreviewLink examId={exams.id} />{' '}
          {exams.examined.surname} {exams.examined.firstname}
        </>
      ),
      'examined.pesel': exams?.examined?.pesel || '',
      'company.name': exams?.examPackage?.company?.name || '',
      'exams.description': exams.description || '',
      // last_change_date: momencik(exams.changeDate, 'YYYY-MM-DD HH:mm:ss') || '',
      'exams.startDate': momencik(exams.startDate, 'YYYY-MM-DD HH:mm:ss') || '',
      'exams.startVersion': exams.startVersion || '',
      'exams.endDate': (
        <>
          {exams.endVersion && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>Wersja stanowiska testów: {exams.endVersion}</Tooltip>
              }
            >
              <Icon.InfoCircle />
            </OverlayTrigger>
          )}{' '}
          {momencik(exams.endDate, 'YYYY-MM-DD HH:mm:ss')}
        </>
      ),
      'exams.status': exams?.status,
      'exams.pin': (
        <OverlayTrigger
          placement="bottom"
          overlay={<Tooltip>{exams.pin}</Tooltip>}
        >
          <Badge
            bg="success"
            className="me-1"
            onClick={e => e.stopPropagation()}
          >
            #
          </Badge>
        </OverlayTrigger>
      ),
      'healthcareFacility.displayName':
        exams?.examPackage?.healthcareFacility?.displayName || '',
      critical_point: (
        <>
          {exams?.criticalPoint && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>
                  Zostały oznaczone punkty krytyczne: {exams.criticalPoint}
                </Tooltip>
              }
            >
              <Badge
                bg="danger"
                className="me-1"
                onClick={e => e.stopPropagation()}
              >
                !
              </Badge>
            </OverlayTrigger>
          )}
          {exams?.hasSynevo && (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Wysłano skierowanie do Synevo</Tooltip>}
            >
              <Badge
                bg="secondary"
                className="me-1"
                onClick={e => e.stopPropagation()}
              >
                S
              </Badge>
            </OverlayTrigger>
          )}
        </>
      ),
      preview: exams?.active && (
        <>
          {exams.endDate && <ExamPreviewLink examId={exams.id} />}
          {exams.startDate &&
            !exams.endDate &&
            process.env.REACT_APP_CHAT_APP_URL && (
              <ExamChatLink examId={exams.id} token={token} />
            )}
        </>
      ),
      remove_action: onRemove && (
        <ExamRemoveFromListLink examId={exams.id} onRemove={onRemove} />
      ),
    },
    isInactive: !exams.active,
  };
};

export const ExamSearch: React.FC<ExamSearchProps> = props => {
  const columns = [
    { header: '', property: 'exams.online', noFilter: true },
    { header: '', property: 'critical_point', noFilter: true },
    { header: 'Numer', property: 'exams.number' },
    { header: 'Badany', property: 'examined.surname' },
    { header: 'PESEL', property: 'examined.pesel' },
    { header: 'Firma', property: 'company.name' },
    { header: 'Opis', property: 'exams.description' },
    { header: 'Pin', property: 'exams.pin' },
    { header: 'Placówka', property: 'healthcareFacility.displayName' },
    { header: 'Początek', property: 'exams.startDate', noFilter: true },
    // { header: 'Wersja stan. testów', property: 'exams.startVersion' },
    { header: 'Koniec', property: 'exams.endDate', noFilter: true },
    {
      header: 'Status',
      property: 'exams.status',
      noSorting: true,
      noFilter: true,
    },
    ...(props.withoutButtons
      ? [{ header: '', property: 'preview', noSorting: true, noFilter: true }]
      : []),
    ...(props.onRemove
      ? [
          {
            header: '',
            property: 'remove_action',
            noSorting: true,
            noFilter: true,
          },
        ]
      : []),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      //pageNumber={4}
      showFilter={true}
      withHistory={true}
      initialOrderColumn={props.initialOrderColumn || 'exams.number'}
      initialOrderDirection={props.initialOrderDirection || 'DESC'}
      availablePageSizes={[10, 20, 50]}
      onRowClick={props.onRowClick}
      emptyGridMessage="Brak zleconych badań w systemie"
      refresh={props.refresh}
      withFilterInColumns={props.withFilterInColumns}
      enableMultiSelect={props.enableMultiSelect}
      selectedKeys={props.selectedKeys}
    />
  );
};
